import { useMutation, useQueryClient } from '@tanstack/react-query'
import { policyCatalogKeys, policyDefinitionKeys, policyOrderKeys, policyResultKeys } from '@/modules/hierarchy'
import { usePrinterPolicyReconciliation } from '../printer-policies/use-printer-policy-reconciliation'
import {
  eventRulePoliciesApi,
  PRINTER_EVENT_RULE_POLICY_TYPE,
  type EventRulePolicyEdit,
  type PrinterEventRulePolicy,
} from '../../api/event-rule-policies-api'

export function useSaveEventRulePolicy(entityId: string) {
  const client = useQueryClient()
  const refreshKeys = [policyCatalogKeys.all(entityId), policyOrderKeys.type(entityId, PRINTER_EVENT_RULE_POLICY_TYPE),
    policyDefinitionKeys.all(entityId), policyResultKeys.type(entityId, PRINTER_EVENT_RULE_POLICY_TYPE)]
  return useMutation({
    mutationKey: ['printer-policy-save', entityId, PRINTER_EVENT_RULE_POLICY_TYPE],
    mutationFn: ({
      policy,
      operationId,
      ruleId,
      edit,
    }: {
      policy: PrinterEventRulePolicy | null
      operationId: string
      ruleId: string
      edit: EventRulePolicyEdit
    }) =>
      policy
        ? eventRulePoliciesApi.update(entityId, policy, edit)
        : eventRulePoliciesApi.create(entityId, operationId, ruleId, edit),
    retry: false,
    onSuccess: async () => {
      await Promise.all(refreshKeys.map((queryKey) => client.cancelQueries({ queryKey })))
      await Promise.all(refreshKeys.map((queryKey) => client.invalidateQueries({ queryKey })))
    },
  })
}

export function useDeleteEventRulePolicy(entityId: string) {
  const deletion = usePrinterPolicyReconciliation(entityId, PRINTER_EVENT_RULE_POLICY_TYPE)
  return useMutation({
    mutationKey: ['printer-policy-delete', entityId, PRINTER_EVENT_RULE_POLICY_TYPE],
    mutationFn: (policy: PrinterEventRulePolicy) =>
      eventRulePoliciesApi.delete(entityId, policy),
    retry: false,
    onSuccess: (_, policy) => deletion.deleted(policy.policyId),
  })
}
